"use client";

import React, { useEffect, useRef, useState } from "react";

/**
 * Visibility gate for expensive (WebGL/canvas) ThreeUI effects.
 *
 * Renders `children` only while the effect is actually worth running:
 *  - within (or near) the viewport — IntersectionObserver, so offscreen effects
 *    are unmounted and stop their render loops (§22, §23);
 *  - the browser tab is visible — pauses on `visibilitychange` (§24);
 *  - the user hasn't asked for reduced motion — falls back to a static layer (§44);
 *  - not on a small phone when `disableOnMobile` — protects mobile CPU/GPU (§46).
 *
 * Because ThreeUI effects are `dynamic(ssr:false)`, gating their mount also defers
 * their JS/WebGL init until the section is approached, cutting initial cost (§49).
 */
export function LazyEffect({
  children,
  fallback = null,
  rootMargin = "280px 0px",
  disableOnMobile = true,
  delayMs = 0,
  className = "",
}: {
  children: React.ReactNode;
  /** Shown instead of the effect when it is gated off (reduced motion / mobile). */
  fallback?: React.ReactNode;
  rootMargin?: string;
  disableOnMobile?: boolean;
  /** Defer mount this long after first entering view (LCP protection for
   *  above-the-fold effects, §51). */
  delayMs?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const [reduced, setReduced] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const [delayPassed, setDelayPassed] = useState(delayMs <= 0);

  // Capability checks (client-only). Re-evaluated on media-query changes.
  useEffect(() => {
    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobileMq = window.matchMedia("(max-width: 640px)");
    const syncReduce = () => setReduced(reduceMq.matches);
    const syncMobile = () => setAllowed(!(disableOnMobile && mobileMq.matches));
    syncReduce();
    syncMobile();
    reduceMq.addEventListener("change", syncReduce);
    mobileMq.addEventListener("change", syncMobile);
    return () => {
      reduceMq.removeEventListener("change", syncReduce);
      mobileMq.removeEventListener("change", syncMobile);
    };
  }, [disableOnMobile]);

  // Viewport + tab-visibility gating.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin },
    );
    io.observe(el);
    const onVisibility = () => setTabVisible(document.visibilityState === "visible");
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [rootMargin]);

  // Defer first mount by delayMs after entering view (once passed, stays passed).
  useEffect(() => {
    if (delayMs <= 0 || delayPassed || !inView) return;
    const t = window.setTimeout(() => setDelayPassed(true), delayMs);
    return () => window.clearTimeout(t);
  }, [inView, delayMs, delayPassed]);

  const active = inView && tabVisible && !reduced && allowed && delayPassed;

  return (
    <div ref={ref} className={className}>
      {active ? children : fallback}
    </div>
  );
}
